/**
 * AudioPlayer — Custom audio player for voice messages in chat bubbles.
 *
 * Loads audio via authenticated API, provides play/pause, seek bar, and duration.
 * Handles WebM Infinity-duration bug and iOS Safari compatibility.
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { Play, Pause, Loader2, FileText } from "lucide-react";
import { request } from "../lib/api";
import styles from "./ChatView.module.css";

interface AudioPlayerProps {
  fileId: string;
  filename: string;
  encryptionKey?: string;
  encryptionIv?: string;
  originalMime?: string;
}

export function AudioPlayer({ fileId, encryptionKey, encryptionIv, originalMime }: AudioPlayerProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [transcribing, setTranscribing] = useState(false);
  const [transcribeError, setTranscribeError] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const durationFixedRef = useRef(false);
  const playResolvedDurationRef = useRef(false);

  // Load audio via authenticated API (with auto token refresh)
  useEffect(() => {
    let cancelled = false;
    let blobUrl: string | null = null;

    async function loadAudio() {
      try {
        // Use request() with raw:true — handles 401 + token refresh automatically
        // Use fetchAndDecryptFile for encrypted files, otherwise use authenticated request
        const mimeType = originalMime ?? "audio/webm";
        if (encryptionKey && encryptionIv) {
          const { fetchAndDecryptFile } = await import("../lib/file-crypto");
          blobUrl = await fetchAndDecryptFile(fileId, mimeType, encryptionKey, encryptionIv);
        } else {
          const res = await request<Response>(`/files/${fileId}`, { raw: true });
          const response = res as unknown as Response;
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const blob = await response.blob();
          const contentType = response.headers.get("Content-Type") || mimeType;
          const typedBlob = new Blob([blob], { type: contentType });
          blobUrl = URL.createObjectURL(typedBlob);
        }
        if (cancelled) return;

        const audio = new Audio();
        audioRef.current = audio;

        // Preload metadata
        audio.preload = "metadata";

        const onLoadedMetadata = () => {
          if (cancelled) return;
          if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
            setDuration(audio.duration);
            durationFixedRef.current = true;
          }
          setLoading(false);
        };

        const onDurationChange = () => {
          if (cancelled) return;
          if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
            setDuration(audio.duration);
            durationFixedRef.current = true;
          }
        };

        const onTimeUpdate = () => {
          if (cancelled) return;
          setCurrentTime(audio.currentTime);

          // Resolve duration from playback if still unknown
          if (!durationFixedRef.current && audio.duration && isFinite(audio.duration) && audio.duration > 0) {
            setDuration(audio.duration);
            durationFixedRef.current = true;
          }
        };

        const onEnded = () => {
          if (cancelled) return;
          setPlaying(false);
          setCurrentTime(0);
          audio.currentTime = 0;

          // If we got duration during playback, keep it
          if (!durationFixedRef.current && playResolvedDurationRef.current) {
            setDuration(audio.currentTime);
          }
        };

        const onCanPlayThrough = () => {
          if (cancelled) return;
          setLoading(false);
          // Try the infinity duration workaround after canplaythrough
          if (!durationFixedRef.current && (!audio.duration || !isFinite(audio.duration))) {
            fixInfinityDuration(audio);
          }
        };

        const onError = () => {
          if (cancelled) return;
          console.error("Audio load error:", audio.error);
          setError(true);
          setLoading(false);
        };

        audio.addEventListener("loadedmetadata", onLoadedMetadata);
        audio.addEventListener("durationchange", onDurationChange);
        audio.addEventListener("timeupdate", onTimeUpdate);
        audio.addEventListener("ended", onEnded);
        audio.addEventListener("canplaythrough", onCanPlayThrough);
        audio.addEventListener("error", onError);

        audio.src = blobUrl;
        audio.load();

        // Fallback: if still loading after 3s, show player anyway
        setTimeout(() => {
          if (!cancelled) {
            setLoading(false);
          }
        }, 3000);
      } catch (err) {
        console.error("AudioPlayer load failed:", err);
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      }
    }

    loadAudio();

    return () => {
      cancelled = true;
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current = null;
      }
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [fileId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch existing transcript on mount
  useEffect(() => {
    let cancelled = false;
    async function fetchTranscript() {
      try {
        const res = await request<{ transcript: string | null }>(`/files/${fileId}/transcript`);
        if (!cancelled && res.transcript) {
          setTranscript(res.transcript);
        }
      } catch {
        // No transcript yet — that's fine
      }
    }
    fetchTranscript();
    return () => { cancelled = true; };
  }, [fileId]);

  const handleTranscribe = useCallback(async () => {
    if (transcribing || transcript) return;
    setTranscribing(true);
    setTranscribeError(false);
    try {
      const res = await request<{ transcript: string }>(`/files/${fileId}/transcribe`, { method: "POST" });
      setTranscript(res.transcript);
    } catch {
      setTranscribeError(true);
    } finally {
      setTranscribing(false);
    }
  }, [fileId, transcribing, transcript]);

  // Workaround for WebM Infinity duration bug (Chrome/Chromium)
  function fixInfinityDuration(audio: HTMLAudioElement) {
    if (durationFixedRef.current) return;

    // Seek to a very large time to force duration resolution
    const prevTime = audio.currentTime;
    audio.currentTime = Number.MAX_SAFE_INTEGER;

    const handler = () => {
      if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
        durationFixedRef.current = true;
      }
      audio.currentTime = prevTime;
      audio.removeEventListener("timeupdate", handler);
    };

    audio.addEventListener("timeupdate", handler);

    // Safety timeout: remove handler after 2s
    setTimeout(() => {
      audio.removeEventListener("timeupdate", handler);
    }, 2000);
  }

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      const playPromise = audio.play();
      if (playPromise) {
        playPromise
          .then(() => {
            setPlaying(true);
            playResolvedDurationRef.current = true;
          })
          .catch((err) => {
            console.error("Play failed:", err);
            setPlaying(false);
          });
      }
    }
  }, [playing]);

  const handleSeek = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const audio = audioRef.current;
      const bar = progressRef.current;
      if (!audio || !bar || !duration) return;

      const rect = bar.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      audio.currentTime = ratio * duration;
      setCurrentTime(audio.currentTime);
    },
    [duration],
  );

  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds) || seconds < 0) return "0:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className={styles.audioPlayer}>
        <Loader2 size={18} className="spin" />
        <span className={styles.audioTime}>Laden…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.audioPlayer}>
        <span className={styles.audioTime}>⚠️ Audio nicht verfügbar</span>
      </div>
    );
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div>
      <div className={styles.audioPlayer}>
        <button
          type="button"
          className={styles.audioPlayBtn}
          onClick={togglePlay}
          aria-label={playing ? "Pause" : "Abspielen"}
        >
          {playing ? <Pause size={16} /> : <Play size={16} />}
        </button>

        <div className={styles.audioProgressWrap} ref={progressRef} onClick={handleSeek}>
          <div className={styles.audioProgressTrack}>
            <div className={styles.audioProgressBar} style={{ width: `${progress}%` }} />
          </div>
        </div>

        <span className={styles.audioTime}>
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </div>

      {transcript ? (
        <div className={styles.audioTranscript}>{transcript}</div>
      ) : (
        <button
          type="button"
          className={styles.transcribeBtn}
          onClick={handleTranscribe}
          disabled={transcribing}
        >
          {transcribing ? (
            <>
              <Loader2 size={12} className="spin" />
              Transkribiere…
            </>
          ) : transcribeError ? (
            <>📝 Transkription fehlgeschlagen – erneut versuchen</>
          ) : (
            <>
              <FileText size={12} />
              Transkribieren
            </>
          )}
        </button>
      )}
    </div>
  );
}
